/*****************************************************
 * Packages
 ******************************************************/
import React, { useContext, useRef, useCallback } from 'react';
import styled, { css } from 'styled-components';
/*****************************************************
 * Locals
 ******************************************************/
import Dropdown, { Option } from './other-components/Dropdown';
import { ElementContext } from '../ElementRenderContext';
import Button from './other-components/Button';
import { prefix } from '../config';
import Tooltip from './Tooltip';

const getNumericAddress = (address) => Number(address.split('.').join(''));

const tooltipText = {
    SECTION: {
        move: 'Drag and Move Section',
        edit: 'Edit Section Style',
        clone: 'Duplicate Section',
        save: 'Save Section into Collection',
        remove: 'Delete Section',
        add: 'Add New Section Below',
    },
    ROW: {
        move: 'Drag and Move Row',
        edit: 'Edit Row Style',
        clone: 'Duplicate Row',
        save: 'Save Row into Collection',
        remove: 'Delete Row',
        add: 'Add New Row Below',
    },
    ELEMENT: {
        move: 'Drag and Move Element',
        edit: 'Edit Element',
        clone: 'Duplicate Element',
        save: 'Save Element into Collection',
        remove: 'Delete Element',
        add: 'Add New Element Below',
    },
    COLUMN: {
        move: 'Drag and Move Column',
        edit: 'Edit Column',
        switch: 'Modify Column Size',
        more: 'Show More Options',
        colSize: 'Quick Change Column Size',
    },
};

/*****************************************************
 * Styled Components
 ******************************************************/
const ActionButton = styled(Button)``;

const Controls = styled.div`
    .add-new-column {
        right: 0;
        top: 50%;
        z-index: 9999;
        position: absolute;
        border-radius: 3px;
        background: rgb(230, 0, 173);
        transform: translateY(-50%);
    }
`;

const FooterControls = styled.div`
    bottom: 0;
    z-index: 9999;
    font-size: 12px;
    position: absolute;
    border-radius: 3px;
    white-space: nowrap;
    color: rgb(255, 255, 255);
    transition: all 0.3s ease 0s;
    background: rgb(43, 53, 86);

    ${getFooterControlPosition}
`;

function getFooterControlPosition({ elType }) {
    switch (elType) {
        case 'ROW': {
            return css`
                left: 50%;
                bottom: -26px;
                transform: translateX(-50%);
            `;
        }
        case 'SECTION':
        default:
            return css`
                right: 0;
            `;
    }
}

function getControlTypeStyle(props) {
    const { item, address } = props;
    const { type: elType } = item;
    if (elType === 'section') {
        const numAddr = getNumericAddress(address);
        return { left: 0, top: numAddr === 0 ? '0' : '-26px' };
    } else if (elType === 'column') {
        return { left: 0, top: 0 };
    } else if (elType === 'row') {
        const numAddr = getNumericAddress(address);
        return {
            left: '50%',
            top: numAddr === 0 ? 0 : '-26px',
            transform: 'translate(-50%)',
        };
    } else {
        return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }
}

function getControlStyle(props) {
    return {
        zIndex: 1000,
        display: 'flex',
        fontSize: '12px',
        borderRadius: '3px',
        position: 'absolute',
        transition: 'all 0.3s',
        whiteSpace: 'nowrap',

        color: props.color.fg,
        background: props.color.bg,
        ...getControlTypeStyle(props),
    };
}

function getButtonStyle(props) {
    const background =
        props && props.item
            ? !isLayoutType(props.item.type)
                ? 'rgba(0,0,0,0.15)'
                : '#0062ff'
            : 'transparent';

    return {
        background,
        border: 0,
        padding: 0,
        color: '#fff',
        minWidth: '26px',
        fontSize: '12px',
        minHeight: '26px',
        cursor: props && props.drag ? 'move' : 'cursor',
    };
}

function isLayoutType(type) {
    return /^(section|row|column)$/.test(type) === false;
}

const ElementControl = (props) => {
    const {
        item,
        color,
        address,
        className,
        onMouseLeave,
        onMouseEnter,
        setBorderType,
        connectDragSource,
        setControlVisibility,
        freezeControlVisibility,
    } = props;

    const {
        onRemove,
        showAddModal,
        onClickSettings,
        duplicateElement,
        showSettingsModal,
    } = useContext(ElementContext);

    const dragHandleRef = useRef();
    connectDragSource(dragHandleRef);

    const handleRemove = useCallback(() => {
        onRemove(address);
    }, [address, onRemove]);

    const handleClickOnSave = useCallback(() => {
        // freezeControlVisibility(true);
        showSettingsModal('SAVE-ELEMENT', address);
    }, [address, showSettingsModal]);

    const handleOnClickEdit = useCallback(() => {
        onClickSettings(address);
        setBorderType('');
    }, [address, onClickSettings, setBorderType]);

    let hasPadding = false;
    const colSize = item?.attr?.__class__columnSize;
    if (item.style && item.style.padding) {
        const padding = new Map(item.style.padding);
        hasPadding = parseInt(padding.get('top')) > 26;
    }

    const patt = /^0\.?(?=[0-9])?0?$/;
    const isFirstSection = patt.test(address);
    const tooltipPlacement = isFirstSection
        ? isLeft(item._elType)
            ? 'bottom-right'
            : 'bottom'
        : isLeft(item._elType)
        ? 'top-right'
        : 'top';

    return (
        <>
            <Controls
                color={color}
                elType={item.type}
                hasPadding={hasPadding}
                onMouseLeave={onMouseLeave}
                onMouseEnter={onMouseEnter}
                style={getControlStyle(props)}
                className={`${prefix}-ec ${prefix}-element-control__controls ${className}`}
            >
                <Tooltip
                    effect="hover"
                    placement={tooltipPlacement}
                    content={tooltipText[item._elType].move}
                >
                    <ActionButton
                        size="xs"
                        type="none"
                        style={getButtonStyle({ drag: true })}
                        innerRef={dragHandleRef}
                        icon={['far', 'arrows-alt']}
                        className={`${prefix}-element-control__action-btn`}
                    />
                </Tooltip>
                {item.type === 'column' && (
                    <Tooltip
                        effect="hover"
                        placement={tooltipPlacement}
                        content={tooltipText[item._elType].colSize}
                    >
                        <ActionButton
                            size="xs"
                            type="none"
                            style={getButtonStyle()}
                            onClick={handleOnClickEdit}
                            className={`${prefix}-element-control__action-btn`}
                        >
                            {colSize}
                        </ActionButton>
                    </Tooltip>
                )}
                <Tooltip
                    effect="hover"
                    placement={tooltipPlacement}
                    content={tooltipText[item._elType].edit}
                >
                    <ActionButton
                        size="xs"
                        type="none"
                        icon={['far', 'edit']}
                        style={getButtonStyle()}
                        onClick={handleOnClickEdit}
                        className={`${prefix}-element-control__action-btn`}
                    />
                </Tooltip>
                {item.type !== 'column' && (
                    <Tooltip
                        effect="hover"
                        placement={tooltipPlacement}
                        content={tooltipText[item._elType].clone}
                    >
                        <ActionButton
                            size="xs"
                            type="none"
                            icon={['far', 'clone']}
                            style={getButtonStyle()}
                            onClick={() => duplicateElement(address)}
                            className={`${prefix}-element-control__action-btn`}
                        />
                    </Tooltip>
                )}

                {item.type !== 'column' && (
                    <Tooltip
                        effect="hover"
                        placement={tooltipPlacement}
                        content={tooltipText[item._elType].save}
                    >
                        <ActionButton
                            size="xs"
                            type="none"
                            icon={['far', 'save']}
                            style={getButtonStyle()}
                            onClick={handleClickOnSave}
                            className={`${prefix}-element-control__action-btn`}
                        />
                    </Tooltip>
                )}
                {item.type !== 'column' && (
                    <Tooltip
                        effect="hover"
                        placement={tooltipPlacement}
                        content={tooltipText[item._elType].remove}
                    >
                        <ActionButton
                            size="xs"
                            type="none"
                            icon={['far', 'trash-alt']}
                            style={getButtonStyle()}
                            onClick={handleRemove}
                            className={`${prefix}-element-control__action-btn`}
                        />
                    </Tooltip>
                )}
                {item.type !== 'column' ? (
                    <Tooltip
                        effect="hover"
                        placement={tooltipPlacement}
                        content={tooltipText[item._elType].add}
                    >
                        <ActionButton
                            icon={['far', 'layer-plus']}
                            style={getButtonStyle(props)}
                            onClick={() => showAddModal(item._elType, address)}
                            className={`${prefix}-element-control__action-btn`}
                        />
                    </Tooltip>
                ) : (
                    <Dropdown
                        color={color}
                        onClick={() => {
                            setBorderType('');
                            setControlVisibility(false);
                        }}
                        button={(toggle) => (
                            <Tooltip
                                effect="hover"
                                placement={tooltipPlacement}
                                content={tooltipText[item._elType].more}
                            >
                                <ActionButton
                                    size="xs"
                                    type="none"
                                    onClick={toggle}
                                    icon={['far', 'ellipsis-v']}
                                    style={getButtonStyle(props)}
                                    className={`${prefix}-element-control__action-btn`}
                                />
                            </Tooltip>
                        )}
                    >
                        <Option
                            icon={['far', 'plus']}
                            onClick={() => showAddModal(item._elType, address)}
                        >
                            New Column
                        </Option>
                        <Option
                            icon={['far', 'clone']}
                            onClick={() => duplicateElement(address)}
                        >
                            Duplicate
                        </Option>
                        <Option
                            onClick={handleClickOnSave}
                            icon={['far', 'save']}
                        >
                            Save
                        </Option>
                        <Option
                            icon={['far', 'trash-alt']}
                            onClick={handleRemove}
                        >
                            Delete Column
                        </Option>
                    </Dropdown>
                )}
            </Controls>
            {item.type === 'column' && (
                <Controls
                    className={`${prefix}-ec ${prefix}-element-control__controls`}
                >
                    <Tooltip
                        className="add-new-column"
                        effect="hover"
                        placement="top"
                        content="Add New Column"
                    >
                        <ActionButton
                            icon={['far', 'layer-plus']}
                            style={getButtonStyle(props)}
                            onClick={() => showAddModal(item._elType, address)}
                            className={`${prefix}-element-control__action-btn`}
                        />
                    </Tooltip>
                </Controls>
            )}
            {item._elType === 'SECTION' ? (
                <FooterControls
                    onMouseLeave={onMouseLeave}
                    onMouseEnter={onMouseEnter}
                    elType={item._elType}
                    className={`${prefix}-ec ${prefix}-element-control__controls`}
                >
                    <Tooltip
                        effect="hover"
                        placement="top-left"
                        content="Add New Section"
                    >
                        <ActionButton
                            icon={['far', 'layer-plus']}
                            style={{
                                ...getButtonStyle(props),
                                padding: '0 6px',
                            }}
                            onClick={() => showAddModal(item._elType, address)}
                            className={`${prefix}-element-control__action-btn`}
                        ></ActionButton>
                    </Tooltip>
                </FooterControls>
            ) : null}
        </>
    );
};

export default React.memo(ElementControl);

function isLeft(elType) {
    return elType === 'SECTION' || elType === 'COLUMN';
}
